import * as React from "react";
import { Button, DataGrid, SelectBox, ValidationGroup, Validator } from "devextreme-react";
import { Column, Button as GridButton } from "devextreme-react/data-grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IGemeente } from "../models/IGemeente";
import { useFetchData, useListSource } from "../components/hooks/DataSource";
import { IApiUrls } from "../models/IApiUrls";
import { IListsModel } from "../models/IHomeModel";
import { IPageLoading } from "../App";
import { RequiredRule } from "devextreme-react/validator";
import useLocationState from "../components/hooks/LocationState";

interface IProps extends IPageLoading {
    apiUrls: IApiUrls
    lists: IListsModel;
}

interface IVisitatieRapportItem {
    jaar: number;
    shmNaam: string;
    activiteit: string;
    visitatieronde: string;
    score: string;
    documentLink: string;
}

interface IFormData {
    gemeenteId?: number;
    shmId?: number;
    searchType?: "Gemeente" | "Shm";
}

const ZoekRapporten = ({ apiUrls, lists, setPageLoading }: IProps) => {

    const resultsRef = useRef<HTMLDivElement>(null);
    const valGroupGemRef = useRef<ValidationGroup>(null);
    const valGroupShmRef = useRef<ValidationGroup>(null);
    const dsGem = useListSource(lists.gemeenten, "naam"); // useUrlListSource(apiUrls.gemeenten);
    const dsShm = useListSource(lists.shms, "naam"); // useUrlListSource(apiUrls.shms);
    const renderGemeenteSelectText = useCallback((item: IGemeente) =>
        item && item.naam + " (" + item.postcode + ")", [])

    const [formData, setFormData] = useLocationState<IFormData>({});    

    const [data, setData] = useState<any[]>([]);
    const [searchMsg, setSearchMsg] = useState("");

    const fetcher = useFetchData();

    const searchData = useCallback((endpoint: string, message: (apiData: any[]) => string) => {
        setPageLoading(true);
        setData([]);
        setSearchMsg("Bezig met zoeken...");
        fetcher(endpoint).then(apiData => {
            setData(apiData);
            setSearchMsg(message(apiData));
            setPageLoading(false);
            resultsRef.current?.scrollIntoView({ behavior: "smooth" })
        }).catch(() => setPageLoading(false));
    }, [setPageLoading, fetcher])

    const searchByGemeente = useCallback(() => {
        valGroupGemRef.current?.instance.validate().isValid && searchData(
            apiUrls.zoekRapportGemeente + "/" + formData.gemeenteId,
            (apiData: any[]) => apiData.length + (apiData.length === 1 ? " resultaat" : " resultaten") + " voor gemeente " + lists.gemeenten?.find(g => g.id === formData.gemeenteId)?.naam
        );
    }, [formData.gemeenteId, apiUrls, lists.gemeenten, searchData])

    const searchByShm = useCallback(() => {
        valGroupShmRef.current?.instance.validate().isValid && searchData(
            apiUrls.zoekRapportShm + "/" + formData.shmId,
            (apiData: any[]) => apiData.length + (apiData.length === 1 ? " resultaat" : " resultaten") + " voor SHM " + lists.shms?.find(s => s.id === formData.shmId)?.naam
        )
    }, [formData.shmId, apiUrls, lists.shms, searchData])

    useEffect(() => {        
        switch (formData.searchType) {
            case "Gemeente": searchByGemeente(); break;
            case "Shm":      searchByShm(); break;
            default: break;
        }
    }, [formData.searchType, searchByGemeente, searchByShm])

    const openDoc = useCallback((e: { column: any, row: { data: IVisitatieRapportItem } }) => {
        document.location = e.row.data.documentLink;
    }, [])

    const gemeenteChangeHandler = useCallback((gemeenteId: number) => setFormData(prev => ({ ...prev, gemeenteId, searchType: undefined })), [setFormData])
    const shmChangeHandler = useCallback((shmId: number) => setFormData(prev => ({ ...prev, shmId, searchType: undefined })), [setFormData])

    const searchGemeenteClickHandler = useCallback(() => setFormData(prev => ({ ...prev, searchType: "Gemeente" })), [setFormData])
    const searchShmClickHandler = useCallback(() => setFormData(prev => ({ ...prev, searchType: "Shm" })), [setFormData])

    const searchExprGemeente = useMemo(() => ["naam", "postcode"], [])
    const searchExprShm = useMemo(() => ["naam", "nummer"], [])

    return <>
        <h2>Hoe presteert de sociale huisvestingsmaatschappij (SHM) in uw buurt?</h2>

        <div className="row search-row">

            <ValidationGroup ref={valGroupGemRef} className="col-md search-box">

                <h4>Zoeken op gemeente</h4>
                <SelectBox
                    dataSource={dsGem}
                    placeholder="Gemeente / Postcode"
                    searchExpr={searchExprGemeente}
                    displayExpr={renderGemeenteSelectText}
                    searchEnabled
                    //applyValueMode="instantly"
                    searchTimeout={200}
                    valueExpr="id"
                    onValueChange={gemeenteChangeHandler}
                    value={formData.gemeenteId}
                    onEnterKey={searchGemeenteClickHandler}                    
                >
                    <Validator>
                        <RequiredRule message="Gemeente is verplicht" />
                    </Validator>
                </SelectBox>
                <Button text="Zoeken" onClick={searchGemeenteClickHandler} width="100%" />

            </ValidationGroup>

            <ValidationGroup ref={valGroupShmRef} className="col-md search-box">
                <h4>Zoeken op SHM</h4>
                <SelectBox
                    dataSource={dsShm}
                    placeholder="SHM naam / SHM nummer"
                    searchExpr={searchExprShm}
                    //displayExpr={renderGemeenteSelectText}
                    displayExpr="naam"
                    searchEnabled
                    searchTimeout={200}
                    valueExpr="id"
                    onValueChange={shmChangeHandler}
                    value={formData.shmId}
                    onEnterKey={searchShmClickHandler}
                >
                    <Validator>
                        <RequiredRule message="Shm is verplicht" />
                    </Validator>
                </SelectBox>

                <Button text="Zoeken" onClick={searchShmClickHandler} width="100%" />
            </ValidationGroup>

        </div>
        <div id="results" ref={resultsRef}>
            <h4>{searchMsg} </h4>
            <DataGrid<IVisitatieRapportItem, number> dataSource={data}>

                <Column dataField="jaar" width="auto" />
                <Column dataField="shmNummer" width="auto" />
                <Column dataField="naamRapport" caption="Rapport" />
                <Column dataField="maatschappelijkeZetelGemeenteNaam" caption="Maatschappelijke zetel" width="auto" />
                <Column dataField="activiteit" width="auto" />
                <Column dataField="visitatieronde" width="auto" />
                {/*<Column dataField="documentLink" />*/}
                <Column type="buttons" width={50}>
                    <GridButton hint="Visitatierapport openen" icon="download" onClick={openDoc} />
                </Column>
            </DataGrid>
        </div>
    </>;
}
export default ZoekRapporten;